import { PenSquareIcon } from 'lucide-react'
import cx from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { Button, Input } from '@bpinternal/ui-kit-next'

type Props = {
  value: string
  onSave: (newValue: string) => Promise<boolean> | boolean
  readonly?: boolean
  emptyButtonText?: string
  onCancel?: () => void
  validate?: (value: string) => boolean
  isValid?: boolean // if defined, it will override the validation result
  onChange?: (value: string) => void
}

export function EditableText(props: Props) {
  const [inputValue, setInputValue] = useState(props.value)
  const [editing, setEditing] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [isValid, setIsValid] = useState(props.isValid ?? true)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (props.isValid !== undefined && props.isValid !== isValid) {
      setIsValid(props.isValid)
    }
  }, [props.isValid])

  useEffect(() => {
    const candidate = inputRef.current?.value ?? ''
    if (!editing || candidate === props.value || !props.validate || props.isValid !== undefined) {
      return
    }
    setIsValid(props.validate?.(candidate))
  }, [editing, inputRef.current?.value])

  function handleEditClick() {
    setEditing(true)
    setInputValue(props.value)
    setTimeout(() => inputRef.current?.focus(), 100)
  }

  function handleSave() {
    if (!isValid || submitting) {
      return
    }

    if (inputValue === props.value) {
      setEditing(false)
      return
    }

    setSubmitting(true)

    const newName = inputValue.trim()

    void Promise.resolve(props.onSave(newName)).then((ok) => {
      if (ok) {
        setEditing(false)
        setInputValue(newName)
      }

      setSubmitting(false)
    })
  }

  if (props.readonly) {
    return (
      <div>
        <span>{props.value ?? 'Not set'}</span>
      </div>
    )
  }

  const cancel = () => {
    setEditing(false)
    setIsValid(true)
    props.onCancel?.()
    setInputValue(props.value)
  }

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      void handleSave()
    } else if (event.key === 'Escape') {
      cancel()
    }
  }

  return (
    <div className={'inline-block'}>
      <div className={cx('flex items-center', !editing ? 'inline-block' : 'hidden')}>
        {props.value && (
          <>
            <div className="text-sm">{props.value}</div>
            <Button variant="ghost" size="icon" onClick={handleEditClick} className="ml-1 h-6 w-6">
              <PenSquareIcon />
            </Button>
          </>
        )}
        {!props.value && (
          <Button size="sm" className="h-6 p-0" variant="link" onClick={handleEditClick}>
            {props.emptyButtonText ?? 'Edit'}
          </Button>
        )}
      </div>
      {editing && (
        <div className={'mr-2 flex gap-2'} data-testid="form">
          <Input
            ref={inputRef}
            placeholder="Enter some text"
            value={inputValue}
            disabled={submitting}
            onChange={(ev) => {
              setInputValue(ev.target.value)
              props.onChange?.(ev.target.value)
            }}
            onKeyDown={handleKeyPress}
          />
          <Button className="inline" size="sm" disabled={!isValid || submitting} onClick={handleSave}>
            Save
          </Button>
          <Button className="inline" size="sm" variant="ghost" disabled={submitting} onClick={cancel}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  )
}
